import { useEffect, useState } from 'react';

import {
  DismissNotificationProps,
  notifications,
  ProcessNotificationProps,
} from '@webclient/lib/agent/endpoints/notifications';
import getDataWithPagination from '@webclient/lib/getDataWithPagination';

import useAppSelector from '@webclient/hooks/useAppSelector';
import useStateUpdates from '@webclient/hooks/useStateUpdates';
import useWorkspace from '@webclient/hooks/useWorkspace';

import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { debounce } from 'lodash';

const LIMIT = 10;
const ORDER = 'DESC';

export function useGetInfiniteNotificationsLazily() {
  const { getNamespacedQueryKey, isLoading: isLoadingWorkspace } =
    useWorkspace();
  const isUserAuthenticated = useAppSelector(
    (state) => state.auth.isUserAuthenticated,
  );
  const queryClient = useQueryClient();

  return useInfiniteQuery({
    queryKey: getNamespacedQueryKey('notifications'),
    queryFn: async ({ pageParam }) => {
      const { data, headers } = await notifications.getNotifications({
        limit: LIMIT,
        order: ORDER,
        page: pageParam,
      });

      queryClient.setQueryData(
        ['activeCount'],
        parseInt(headers['x-filtered-count'] as string) || 0,
      );

      const totalCount = headers['x-total-count'] as string;

      const dataWithPagination = getDataWithPagination({
        data,
        totalCount,
        pageParam,
        limit: LIMIT,
      });

      return dataWithPagination;
    },
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (lastPage.data.length === 0) return undefined;

      if (!('pagination' in lastPage.meta)) return undefined;

      if (lastPageParam === lastPage.meta.pagination.pageCount)
        return undefined;

      return lastPageParam + 1;
    },
    getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
      if (firstPageParam <= 2) return undefined;

      return firstPageParam - 1;
    },
    initialPageParam: 1,
    enabled: isUserAuthenticated && !isLoadingWorkspace,
    staleTime: Infinity,
    // retry: false,
  });
}

export function useGetActiveNotificationsCount() {
  const queryClient = useQueryClient();
  const { handleSubscribe } = useStateUpdates();
  const { getNamespacedQueryKey } = useWorkspace();
  const activeCount = queryClient.getQueryData<number>(['activeCount']) || 0;
  const [notificationCount, setNotificationCount] = useState(activeCount);

  useGetInfiniteNotificationsLazily();

  useEffect(() => {
    setNotificationCount(activeCount);
  }, [activeCount]);

  useEffect(() => {
    const debouncedInvalidation = debounce(() => {
      queryClient.invalidateQueries({
        queryKey: getNamespacedQueryKey('notifications'),
      });
    }, 2000);

    const unsubscribe = handleSubscribe({
      type: /notification\./,
      callback: (m) => {
        m.type === 'notification.create' &&
          setNotificationCount((prev) => prev + 1);
        debouncedInvalidation();
      },
    });

    return unsubscribe;
  }, [getNamespacedQueryKey, handleSubscribe, queryClient]);

  return notificationCount;
}

type Props = {
  onSuccess:
    | ((
        data: AxiosResponse<unknown, unknown> | undefined,
        context: unknown,
      ) => Promise<unknown> | unknown)
    | undefined;
};

export const useProcessNotification = (props: Props) => {
  const queryClient = useQueryClient();

  const { getNamespacedQueryKey } = useWorkspace();

  return useMutation({
    mutationFn: async ({
      notificationId,
      action,
    }: ProcessNotificationProps) => {
      const response = await notifications.processNotification({
        notificationId,
        action,
      });

      return response.data;
    },
    onSuccess: (data, _, context) => {
      queryClient.invalidateQueries({
        queryKey: getNamespacedQueryKey('notifications'),
      });

      props.onSuccess && props.onSuccess(data, context);
    },
  });
};

export const useDismissNotification = () => {
  const queryClient = useQueryClient();
  const { getNamespacedQueryKey } = useWorkspace();

  return useMutation({
    mutationFn: async ({ notificationId }: DismissNotificationProps) => {
      const response = await notifications.dismissNotification({
        notificationId,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getNamespacedQueryKey('notifications'),
      });
    },
  });
};
